import { useRef, useState } from "react";
import {
  Cancel01Icon,
  CloudUploadIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { toast } from "react-toastify";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { AsyncSelect } from "shared/components/AsyncSelect/AsyncSelect";
import { loadPropertyOptions } from "api/landlord/asyncUnitProperties";
import { getErrorMessage } from "utils/error";
import { MhvillageExternalMapping } from "api/mhvillage";
import {
  useImportExternalMappings,
  useImportExternalMappingsPreview,
  useUpdateExternalMapping
} from "../api/hooks";

interface ImportCsvModalProps {
  opened: boolean;
  communityLabel: string;
  communitiesLabel: string;
  mappings: MhvillageExternalMapping[];
  onClose: () => void;
}

interface ParsedRow {
  externalId: string;
  propertyId: number | null;
  propertyLabel?: string;
}

// Mirrors CsvParsing::MAX_FILE_BYTES so an oversized file is rejected before the upload.
const MAX_FILE_BYTES = 5 * 1024 * 1024;

export const ImportCsvModal = ({
  opened,
  communityLabel,
  communitiesLabel,
  mappings,
  onClose
}: ImportCsvModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<"upload" | "assign">("upload");
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: previewImport, isPending: isPreviewing } =
    useImportExternalMappingsPreview();
  const { mutateAsync: importMappings, isPending: isImporting } =
    useImportExternalMappings();
  const { mutateAsync: updateMapping } = useUpdateExternalMapping();

  // Bumped whenever the modal is closed, so a preview request that resolves after the user
  // already cancelled (this component stays mounted while "closed" — Modal only hides it) can
  // tell it's stale and skip applying setRows/setStep on top of the just-reset state.
  const attemptRef = useRef(0);

  const reset = () => {
    setStep("upload");
    setFile(null);
    setRows([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    attemptRef.current += 1;
    reset();
    onClose();
  };

  const handleFileChange = (selected: File | null) => {
    if (selected && selected.size > MAX_FILE_BYTES) {
      toast.error("That file is too large — the maximum size is 5MB.");
      if (inputRef.current) inputRef.current.value = "";
      setFile(null);
      return;
    }
    setFile(selected);
  };

  // Parses server-side (no client-side CSV parsing) so the assign step below always works off
  // the backend's authoritative External IDs, without committing anything yet.
  const handleContinue = async () => {
    if (!file) return;

    const attempt = attemptRef.current;
    try {
      const { external_ids: externalIds } = await previewImport(file);
      if (attempt !== attemptRef.current) return;

      const mappingsByExternalId = new Map(
        mappings.map((mapping) => [mapping.external_id, mapping])
      );
      setRows(
        externalIds.map((externalId) => {
          const existing = mappingsByExternalId.get(externalId);
          return {
            externalId,
            propertyId: existing?.property_id ?? null,
            propertyLabel: existing?.property_name ?? undefined
          };
        })
      );
      setStep("assign");
    } catch (error) {
      if (attempt !== attemptRef.current) return;
      toast.error(getErrorMessage(error));
    }
  };

  const handleRowPropertyChange = (
    index: number,
    propertyId: number | null,
    propertyLabel?: string
  ) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, propertyId, propertyLabel } : row
      )
    );
  };

  const handleImport = async () => {
    if (!file) return;

    setIsSubmitting(true);
    try {
      // Import already committed by this point — use allSettled (not all) so one rejected
      // PATCH below doesn't hide the others that succeeded.
      const imported = await importMappings(file);

      // Match by external_id: both sides are server-parsed now (preview populated `rows`,
      // import produced `imported`), so a plain string match is reliable — no need to depend
      // on the two calls returning rows in the same order.
      const importedByExternalId = new Map(
        imported.map((mapping) => [mapping.external_id, mapping])
      );
      const assignments = rows
        .filter((row) => row.propertyId !== null)
        .map((row) => ({
          row,
          mapping: importedByExternalId.get(row.externalId)
        }))
        .filter(
          (
            entry
          ): entry is { row: ParsedRow; mapping: MhvillageExternalMapping } =>
            entry.mapping !== undefined
        );

      const results = await Promise.allSettled(
        assignments.map(({ row, mapping }) =>
          updateMapping({
            id: mapping.id,
            payload: { property_id: row.propertyId }
          })
        )
      );
      const failedCount = results.filter(
        (result) => result.status === "rejected"
      ).length;

      if (failedCount > 0)
        toast.error(
          `Imported ${imported.length} external IDs, but failed to assign ${failedCount} ${communitiesLabel.toLowerCase()}. Assign them from the table.`
        );
      else toast.success("External IDs imported");

      handleClose();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusy = isImporting || isSubmitting;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      panelClassName="max-w-[520px] w-full"
    >
      {step === "upload" ? (
        <div className="relative">
          <div className="px-6 pt-6">
            <h2 className="text-xl font-bold text-content-1000">
              Import external IDs from CSV
            </h2>
            <p className="mt-2 text-base text-content-400">
              Each line in the file becomes a new row. You&apos;ll assign{" "}
              {communitiesLabel.toLowerCase()} after the import.
            </p>
          </div>

          <div className="px-6 py-6">
            <input
              ref={inputRef}
              id="mhvillage-csv-input"
              type="file"
              accept=".csv"
              className="sr-only"
              onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
            />
            <label
              htmlFor="mhvillage-csv-input"
              className="flex flex-col items-center justify-center gap-2 p-4 w-full h-[200px] rounded-lg border border-dashed border-primary-500 bg-primary-20 cursor-pointer"
            >
              <HugeiconsIcon
                icon={CloudUploadIcon}
                size={24}
                className="text-primary-500"
              />
              <span className="text-sm font-medium text-primary-500">
                {file ? file.name : "Upload CSV"}
              </span>
            </label>
          </div>

          <div className="flex justify-end gap-2 px-6 py-4 border-t border-alpha-100">
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.BASE}
              onClick={handleClose}
              type="button"
            >
              Cancel
            </Button>
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.PRIMARY}
              disabled={!file}
              loading={isPreviewing}
              onClick={handleContinue}
              type="button"
            >
              Continue
            </Button>
          </div>
        </div>
      ) : (
        <div className="relative">
          <div className="flex items-center justify-between px-6 py-3 border-b border-alpha-100">
            <h2 className="text-xl font-bold text-content-1000">
              Assign {communitiesLabel.toLowerCase()}
            </h2>
            <IconButton
              variant={IconButtonVariant.BASE}
              size={IconButtonSize.SM}
              onClick={handleClose}
              HugeIcon={Cancel01Icon}
              borderLess
            />
          </div>

          <div className="px-6 py-4 max-h-[420px] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4 pb-2 text-sm text-content-400">
              <span>External ID</span>
              <span>{communityLabel}</span>
            </div>
            {rows.map((row, index) => (
              <div
                key={`${row.externalId}-${index}`}
                className="grid grid-cols-2 gap-4 items-center py-3 border-t border-alpha-100"
              >
                <span className="text-base text-content-1000 truncate">
                  {row.externalId}
                </span>
                <AsyncSelect
                  id={`import-community-${index}`}
                  isClearable={false}
                  searchable
                  menuPosition="fixed"
                  placeholder={`Select ${communityLabel.toLowerCase()}`}
                  value={row.propertyId ?? ""}
                  options={
                    row.propertyId && row.propertyLabel
                      ? [{ value: row.propertyId, label: row.propertyLabel }]
                      : []
                  }
                  // @ts-expect-error -- loadPropertyOptions is loosely typed like our other async selects
                  loadOptions={loadPropertyOptions}
                  additional={{ page: 1 }}
                  onChange={(e) => {
                    if (!Array.isArray(e))
                      handleRowPropertyChange(
                        index,
                        e.target.value ? Number(e.target.value) : null,
                        e.label
                      );
                  }}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 px-6 py-4 border-t border-alpha-100">
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.BASE}
              onClick={handleClose}
              type="button"
            >
              Cancel
            </Button>
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.PRIMARY}
              loading={isBusy}
              onClick={handleImport}
              type="button"
            >
              Import
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
